'use client'

import React, { useId } from 'react'
import { InputBase, InputLabel } from '../InputBase/InputBase'
import type { WithInputBaseProps } from '../InputBase/InputBase'
import { cn } from '@/utils'

/**
 * InputTextProps is the type for the props of the InputText component.
 */
export type InputTextProps = {
  /**
   * Label to be displayed above the input
   */
  label?: string
} & WithInputBaseProps<React.ComponentProps<'input'>>

export const InputText = ({
  className,
  startAdornment,
  endAdornment,
  size,
  style,
  isRounded,
  variant,
  helperText,
  state,
  placeholder,
  label,
  ...props
}: InputTextProps) => {
  const triggerId = useId()
  const inputId = props.id || triggerId

  return (
    <InputBase
      size={size}
      variant={variant}
      isRounded={isRounded}
      state={state}
      disabled={props.disabled}
      helperText={helperText}
      startAdornment={startAdornment}
      endAdornment={endAdornment}
      className={cn('p-0', className)}
      style={style}
    >
      <input
        className={cn(
          'peer h-full w-full p-4 text-sm outline-transparent lg:text-xs',
          {
            'pl-12': !!startAdornment,
            'pr-12': !!endAdornment,
            'not-focus:placeholder:text-transparent': !!label,
            'pt-8': !!label,
          },
        )}
        placeholder={placeholder || ' '}
        data-testid="input"
        {...props}
        disabled={props.disabled ?? false}
        id={inputId}
      />

      {label && (
        <InputLabel state={state} htmlFor={inputId}>
          {label || placeholder || ''}
        </InputLabel>
      )}
    </InputBase>
  )
}
